import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Input, Spinner } from "reactstrap";
import { debounce } from "lodash";
import {
  getAllReachableServers,
  getAuthorizedGroups,
  getCloudServerByHostname,
  getServerByHostname,
} from "../../../../services/serversService";
import useServerReachability from "./useServerReachability";

/**
 * Searchable input for picking a server hostname.
 *
 * Props:
 *   - `value`        current hostname
 *   - `mode`         "onprem" | "cloud"
 *   - `onSelect`     `(hostname) => void` — fires once a hostname resolves
 *   - `onReachable`  optional `(server, isReachable) => void`
 *   - `placeholder`  optional input placeholder
 *
 * Search is debounced. Enter or a dropdown click resolves the hostname;
 * `onSelect` fires only on a successful resolution. Reachability is shown
 * as a non-blocking badge.
 */
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_BADGE = {
  pinging: { color: "secondary", icon: <Spinner size="sm" />, label: "Pinging…" },
  reachable: { color: "success", icon: <i className="fas fa-check-circle mr-1" />, label: "Reachable" },
  unreachable: { color: "warning", icon: <i className="fas fa-exclamation-triangle mr-1" />, label: "Unreachable" },
  error: { color: "danger", icon: <i className="fas fa-times-circle mr-1" />, label: "Ping error" },
};

export default function ServerPicker({ value = "", mode = "onprem", onSelect, onReachable, placeholder }) {
  const [query, setQuery] = useState(value ?? "");
  const [results, setResults] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // `null` = not loaded yet; `[]` = loaded empty.
  const [authorizedGroups, setAuthorizedGroups] = useState(null);
  const [authorizedGroupsError, setAuthorizedGroupsError] = useState(null);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);

  const containerRef = useRef(null);
  const { status: reachabilityStatus, ping: pingServer, reset: resetReachability } = useServerReachability();
  const onReachableRef = useRef(onReachable);
  onReachableRef.current = onReachable;
  // Monotonic counter bumped on every user pick; used by the async
  // resolve+ping branch to drop stale work after a newer pick.
  const pickEpochRef = useRef(0);
  // Last hostname selected via the picker; used to suppress the `value`-sync
  // effect while typing, so an external `value` change doesn't yank the input.
  const lastSelectedRef = useRef(value ?? "");

  // Sync the input from `value` only when not focused and the parent's
  // value diverged from our last selection.
  useEffect(() => {
    if (inputFocused) return;
    if (value === lastSelectedRef.current) return;
    lastSelectedRef.current = value ?? "";
    setQuery(value ?? "");
    resetReachability();
  }, [value, inputFocused, resetReachability]);

  // Reset reachability when the mode changes; previous ping is no longer valid.
  useEffect(() => {
    resetReachability();
  }, [mode, resetReachability]);

  // Fetch the authorized groups once. On failure, set an empty list and
  // surface a visible error so the empty result set isn't indistinguishable
  // from "no matching servers".
  useEffect(() => {
    let cancelled = false;
    getAuthorizedGroups()
      .then((res) => {
        if (cancelled) return;
        setAuthorizedGroups(res?.data?.groups ?? []);
        setAuthorizedGroupsError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setAuthorizedGroups([]);
        setAuthorizedGroupsError(
          "Could not load the list of authorized server groups — search will return no results. Reload the page or contact support."
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Click-outside to close the dropdown.
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const runSearch = useCallback(
    async (search) => {
      if (!search || search.trim().length < 1) {
        setResults([]);
        setIsDropdownOpen(false);
        return;
      }
      // Skip while authorized groups are still loading.
      if (authorizedGroups === null) return;
      setSearching(true);
      try {
        const response = await getAllReachableServers({
          search,
          groups: authorizedGroups,
          mode,
        });
        setResults(response?.data?.results ?? []);
        setIsDropdownOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [authorizedGroups, mode]
  );

  const debouncedSearch = useMemo(() => debounce(runSearch, SEARCH_DEBOUNCE_MS), [runSearch]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleQueryChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    if (resolveError) setResolveError(null);
    debouncedSearch(next);
  };

  const resolveAndPing = useCallback(
    async (hostname) => {
      if (!hostname) return;
      // Resolve before committing so a typo cannot persist an unknown value.
      pickEpochRef.current += 1;
      const epoch = pickEpochRef.current;
      setResolveError(null);
      setResolving(true);
      const resolver = mode === "cloud" ? getCloudServerByHostname : getServerByHostname;
      try {
        const { data: server } = await resolver(hostname);
        if (epoch !== pickEpochRef.current) return; // superseded — abandon this branch.
        lastSelectedRef.current = hostname;
        onSelect?.(hostname);
        const isReachable = await pingServer(hostname, mode);
        if (epoch !== pickEpochRef.current) return;
        if (isReachable !== null && onReachableRef.current) {
          onReachableRef.current(server, isReachable);
        }
      } catch {
        if (epoch !== pickEpochRef.current) return;
        resetReachability();
        setResolveError(`No server found matching "${hostname}".`);
      } finally {
        if (epoch === pickEpochRef.current) setResolving(false);
      }
    },
    [mode, onSelect, pingServer, resetReachability]
  );

  const handlePickResult = (hostname) => {
    setQuery(hostname);
    setIsDropdownOpen(false);
    resolveAndPing(hostname);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setIsDropdownOpen(false);
      resolveAndPing(query.trim());
    } else if (e.key === "Escape") {
      setIsDropdownOpen(false);
    }
  };

  const badge = STATUS_BADGE[reachabilityStatus];

  return (
    <div ref={containerRef} className="position-relative">
      <label className="text-muted small d-block mb-1">Server hostname</label>
      <Input
        value={query}
        onChange={handleQueryChange}
        onKeyDown={handleInputKeyDown}
        onFocus={() => {
          setInputFocused(true);
          if (results.length > 0) setIsDropdownOpen(true);
        }}
        onBlur={() => setInputFocused(false)}
        placeholder={placeholder ?? (mode === "cloud" ? "Search a cloud server" : "Search an on-premise NUC")}
        aria-label="Server hostname"
      />

      {isDropdownOpen && results.length > 0 && (
        <ul className="dropdown-menu show w-100" style={{ top: "100%", maxHeight: "14rem", overflowY: "auto" }}>
          {results.map((server) => (
            <li
              key={server.id ?? server.hostname}
              className="dropdown-item"
              role="button"
              tabIndex={0}
              // onMouseDown so the pick fires before onBlur closes the dropdown.
              onMouseDown={(e) => {
                e.preventDefault();
                handlePickResult(server.hostname);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handlePickResult(server.hostname);
                }
              }}
              style={{ cursor: "pointer" }}
            >
              {server.hostname}
            </li>
          ))}
        </ul>
      )}

      <div className="d-flex align-items-center mt-2" style={{ gap: "0.6rem", minHeight: "1.5rem" }}>
        {searching && (
          <small className="text-muted">
            <Spinner size="sm" className="mr-1" />
            Searching…
          </small>
        )}
        {!searching && resolving && (
          <small className="text-muted">
            <Spinner size="sm" className="mr-1" />
            Resolving…
          </small>
        )}
        {!searching && !resolving && value && (
          <small className="text-muted">
            Current: <strong>{value}</strong>
          </small>
        )}
        {badge && (
          <Badge color={badge.color} pill>
            {badge.icon}
            {badge.label}
          </Badge>
        )}
      </div>

      {authorizedGroupsError && (
        <Alert color="danger" className="mt-2 mb-0 small">
          <i className="fas fa-exclamation-circle mr-2" />
          {authorizedGroupsError}
        </Alert>
      )}
      {resolveError && !authorizedGroupsError && (
        <Alert color="danger" className="mt-2 mb-0 small">
          <i className="fas fa-exclamation-circle mr-2" />
          {resolveError}
        </Alert>
      )}
    </div>
  );
}
